// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Keep: the draft on the bench becomes a machine on the account (M13-20, 11 §4).
//
// THE ORIGINAL IS THE BUILDER'S OWN SAVE, `src/builder/PublishPanel.tsx`'s `save()`
// ------------------------------------------------------------------------------------
// `createMachine → saveVersion`, with idempotency keys, which is exactly the sequence M13-20 names
// and the one the Builder runs when an author presses Save on a machine that has no id yet. The
// server does the deciding — Pro, the author's standing, the fifty-machine and two-hundred-save
// caps, the revalidation of the bytes as received, and `saved_by`, which it records from the
// request's client kind (`webxr` here, because `useAccount.setImmersive` is on while a headset
// session runs; `quest-native` on the native client) for the moderation queue. Nothing here
// decides any of that; it asks, and says what it was told.
//
// **This is the browser's copy of `App/Xr/Build/Keeping.cs`**, which landed first (19 Sep 2026,
// Phase 3 P3-B+) — the one time in Phase 3 the native client was the original. Same rules, same
// sentences, same order of refusals, so a headset on either client says the same thing to the same
// press.
//
// **The title is the machine's own with " remix" after it**, the server's `defaultRemixTitle`
// rule. A slug is unique across the whole arcade, and a bench draft begins as a copy of the
// machine the author was playing — a Keep titled "NEON NOVA" would be refused for the shipped
// machine's name. The kept definition is retitled to match and carries the new machine's slug and
// version, so the document names the row it lives in.
//
// **One machine per Build session.** The first Keep creates it; every Keep after that saves the
// same version again — a draft may be overwritten as often as an author likes (the server's own
// rule), so "Keep" pressed twice is one machine with the latest bytes, not two machines.
//
// **Validated here first, as the MCP tool does**: a document the validator refuses is not sent,
// and the refusal is the validator's own sentence rather than a round trip's.
//
// **On the anonymous side of FR-030.** This module may not name `src/net/client.ts` — it opens
// connections, and `check-anonymous.mjs` walks every import from `XRRuntime`. So the API arrives
// through `KeepPort`, a function the shell supplies and answers through `import('../account')` at
// the press, exactly as `MachineStage` reaches the microphone for `onVoiceMute`; the key builder
// is `src/net/idempotency.ts`, which opens nothing. `PinballApi` satisfies `KeepApi` as it is.
//
// Pure apart from the API it is handed: no DOM, no runtime, no clock.

import { idempotencyKey } from '../../net/idempotency'
import type { ApiError } from '../../net/contract'
import { overBudget, validateMachine, type MachineDefinition } from '@core/machine'
import { BENCH_EN, benchCount, benchText, type BenchStrings } from './benchStrings'

/** What one of the two requests came back as — `ApiResult<Record<string, unknown>>`, named here so the front door need not import the client. */
export type KeepAnswer =
  | { readonly kind: 'ok'; readonly data: Readonly<Record<string, unknown>>; readonly requestId: string }
  | { readonly kind: 'failed'; readonly error: ApiError; readonly requestId?: string }
  | { readonly kind: 'offline'; readonly reason: string }

/** The two calls a Keep makes. `PinballApi` has both, with these signatures. */
export interface KeepApi {
  createMachine(machine: {
    readonly title: string
    readonly era?: string
    readonly idempotencyKey: string
  }): Promise<KeepAnswer>
  saveVersion(save: {
    readonly machineId: string
    readonly semver: string
    readonly definition: Readonly<Record<string, unknown>>
    readonly idempotencyKey: string
  }): Promise<KeepAnswer>
}

/** The arcade, as the shell found it at the press: a client, and whether there is an account to keep into. */
export interface KeepBackend {
  readonly api: KeepApi
  readonly signedIn: boolean
}

/**
 * How a session reaches the arcade for a Keep. Answered at the press, not at construction, so a
 * player who signed in on the page after the machine mounted is signed in for their Keep; an
 * `undefined` answer is a build with no backend, and Keep says so.
 */
export type KeepPort = () => Promise<KeepBackend | undefined>

export const FIRST_VERSION = '1.0.0'

/** The server's own remix suffix, and the database's own title limit. */
export const KEEP_SUFFIX = ' remix'
export const KEEP_TITLE_LIMIT = 60


/** What one press of Keep came to. */
export interface KeepOutcome {
  readonly kept: boolean
  /** One sentence for the author. */
  readonly says: string
  /** One line for the console: request ids, codes, what was sent. */
  readonly log: string
  readonly machineId?: string
  readonly title?: string
  readonly semver?: string
  readonly warnings: number
}

/** The machine a Build session keeps into, once it exists. */
export class KeepSession {
  /** The server's row id, or undefined until the first Keep created it. */
  machineId: string | undefined
  /** The slug the server gave the machine. */
  slug: string | undefined
  title: string | undefined
  semver: string = FIRST_VERSION
  /** How many times a Keep has written this session's machine. */
  kept = 0

  created(machineId: string, slug: string, title: string, firstVersion: string | undefined): void {
    this.machineId = machineId
    this.slug = slug
    this.title = title
    if (firstVersion !== undefined && firstVersion.length > 0) this.semver = firstVersion
  }

  /** The machine is gone (the server said not found): the next Keep creates again. */
  forget(): void {
    this.machineId = undefined
    this.slug = undefined
    this.title = undefined
    this.semver = FIRST_VERSION
    // The machine the next Keep makes is a new one: its first Keep is "Kept as", not "again".
    this.kept = 0
  }

  saved(semver: string): void {
    this.semver = semver
    this.kept += 1
  }

  /** The version to write moved on (the last one was published): the next Keep writes this one. */
  renumber(semver: string): void {
    this.semver = semver
  }
}

/** The kept machine's title: the draft's own, with the remix suffix, inside the limit. */
export function keepTitleFor(definition: MachineDefinition | undefined): string {
  const raw = definition?.meta?.title
  let title = (typeof raw === 'string' ? raw : 'Machine').trim()
  if (title.length === 0) title = 'Machine'
  const room = KEEP_TITLE_LIMIT - KEEP_SUFFIX.length
  if (title.length > room) title = title.slice(0, room).trimEnd()
  return `${title}${KEEP_SUFFIX}`
}

/** The title tried after a name clash: a number after it, inside the limit. */
export function retitled(title: string, attempt: number): string {
  const tail = ` ${attempt}`
  const room = KEEP_TITLE_LIMIT - tail.length
  const head = title.length > room ? title.slice(0, room).trimEnd() : title
  return `${head}${tail}`
}

/**
 * A short, stable fingerprint of a definition's text (FNV-1a 64), for the save key.
 *
 * Over UTF-16 code units, as the native `Keeping.Fingerprint` is over `char`s — though the two
 * clients serialise differently and their keys are never compared; a key only has to be the same
 * for the same bytes from the same client, so a retry after a dropped answer is the same save.
 */
export function fingerprint(text: string): string {
  const prime = 1099511628211n
  const mask = 0xffffffffffffffffn
  let hash = 14695981039346656037n
  for (let i = 0; i < text.length; i++) {
    hash ^= BigInt(text.charCodeAt(i))
    hash = (hash * prime) & mask
  }
  return hash.toString(16).padStart(16, '0')
}

/** The draft, retitled and renamed for the machine it is kept into. */
export function retargeted(
  definition: MachineDefinition,
  slug: string,
  title: string,
  semver: string,
): MachineDefinition {
  return {
    ...definition,
    machineId: slug,
    machineVersion: semver,
    meta: { ...definition.meta, title },
  }
}

export function keptLine(title: string, semver: string, warnings: number, timesBefore: number, strings: BenchStrings = BENCH_EN): string {
  let head = benchText(strings, timesBefore === 0 ? 'keep.keptAs' : 'keep.keptAgain', { title, semver })
  // "in the Builder", not "to read in the Builder": the panel is two rows of forty-two glyphs, and
  // the kept-again line with a warning count has to fit them for a title of ordinary length.
  if (warnings > 0) head += ` ${benchCount(strings, 'keep.warningOne', 'keep.warningMany', warnings)}`
  return head
}

/** Why the server said no, in its words, or in the validator's. */
export function refusedLine(message: string, strings: BenchStrings = BENCH_EN): string {
  return benchText(strings, 'keep.notKept', { message })
}

/**
 * The next patch version — the server's `nextPatch` — for a version that has been published under
 * the author's feet and can no longer be written.
 */
export function nextPatch(semver: string | undefined): string {
  const parts = (semver ?? '').split('.')
  if (parts.length !== 3) return FIRST_VERSION
  const patch = Number.parseInt(parts[2] ?? '', 10)
  if (!Number.isInteger(patch)) return FIRST_VERSION
  return `${parts[0]}.${parts[1]}.${patch + 1}`
}

function refused(says: string, log: string, session?: KeepSession, semver?: string): KeepOutcome {
  return {
    kept: false,
    says,
    log,
    ...(session?.machineId === undefined ? {} : { machineId: session.machineId }),
    ...(session?.title === undefined ? {} : { title: session.title }),
    ...(semver === undefined ? {} : { semver }),
    warnings: 0,
  }
}

function errorLine(error: ApiError): string {
  return `${error.code}: ${error.message}`
}

/**
 * One press of Keep: validate, create the machine if this session has none, save the draft as its
 * version. Every refusal is a sentence; nothing throws for a server's answer.
 */
export async function keepDraft(
  backend: KeepBackend | undefined,
  definition: MachineDefinition | undefined,
  session: KeepSession | undefined,
  strings: BenchStrings = BENCH_EN,
): Promise<KeepOutcome> {
  if (definition === undefined || session === undefined) return refused(strings['keep.nothing'], 'keep: no draft')
  if (backend === undefined) return refused(strings['keep.offline'], 'keep: no api')
  if (!backend.signedIn) return refused(strings['keep.notSignedIn'], 'keep: not signed in')
  const api = backend.api

  // Validated here first, as the MCP tool does: a document the validator refuses is not sent.
  const report = validateMachine(definition)
  if (report.errors.length > 0) {
    const first = report.errors[0]!
    return refused(
      refusedLine(`${first.path} — ${first.message}`, strings),
      `keep: the validator refused the draft (${report.errors.length} error(s)); nothing sent`,
    )
  }
  const over = overBudget(definition)
  if (over.length > 0) {
    const first = over[0]!
    return refused(refusedLine(`${first.path} — ${first.message}`, strings), `keep: over budget (${first.path}); nothing sent`)
  }

  let log = 'keep:'
  if (session.machineId === undefined) {
    const title = keepTitleFor(definition)
    for (let attempt = 1; attempt <= 3; attempt++) {
      const tried = attempt === 1 ? title : retitled(title, attempt)
      const created = await api.createMachine({ title: tried, idempotencyKey: idempotencyKey('keep-machine', tried) })
      if (created.kind === 'offline') {
        return refused(strings['keep.offline'], `${log} create offline — ${created.reason}`)
      }
      if (created.kind === 'failed') {
        log += ` create “${tried}” refused ${errorLine(created.error)} (${created.requestId ?? '-'})`
        // The slug is taken: the same machine under the next name. Anything else is the server's
        // decision about this author, said in its words.
        if (created.error.code === 'conflict' && attempt < 3) continue
        return refused(refusedLine(created.error.message, strings), log)
      }
      const machine = created.data['machine'] as { id?: unknown; slug?: unknown; title?: unknown } | undefined
      const id = typeof machine?.id === 'string' ? machine.id : ''
      if (id.length === 0) {
        return refused(
          refusedLine(strings['keep.noId'], strings),
          `${log} create answered without an id (${created.requestId})`,
        )
      }
      const firstVersion = created.data['firstVersion']
      session.created(
        id,
        typeof machine?.slug === 'string' ? machine.slug : id,
        typeof machine?.title === 'string' ? machine.title : tried,
        typeof firstVersion === 'string' ? firstVersion : undefined,
      )
      log += ` created “${session.title}” as ${session.slug} (${created.requestId})`
      break
    }
    if (session.machineId === undefined) {
      return refused(refusedLine(strings['keep.namesTaken'], strings), log)
    }
  }

  const machineId = session.machineId
  let kept = retargeted(definition, session.slug ?? machineId, session.title ?? keepTitleFor(definition), session.semver)
  let text = JSON.stringify(kept)
  for (let attempt = 1; attempt <= 2; attempt++) {
    const semver = session.semver
    const saved = await api.saveVersion({
      machineId,
      semver,
      definition: kept as unknown as Record<string, unknown>,
      idempotencyKey: idempotencyKey('keep-version', machineId, semver, fingerprint(text)),
    })
    if (saved.kind === 'offline') {
      return refused(strings['keep.offline'], `${log} save offline — ${saved.reason}`, session, semver)
    }
    if (saved.kind === 'failed') {
      log += ` save ${semver} refused ${errorLine(saved.error)} (${saved.requestId ?? '-'})`
      if (saved.error.code === 'not_found') {
        // The machine went away under us (deleted on the web): the next Keep creates again.
        session.forget()
        return refused(refusedLine(saved.error.message, strings), log)
      }
      if (saved.error.code === 'conflict' && attempt === 1) {
        // Published under our feet: the server names the next version; take it.
        session.renumber(nextPatch(semver))
        kept = retargeted(definition, session.slug ?? machineId, session.title ?? keepTitleFor(definition), session.semver)
        text = JSON.stringify(kept)
        continue
      }
      return refused(refusedLine(saved.error.message, strings), log, session, semver)
    }
    const timesBefore = session.kept
    session.saved(semver)
    const warnings = saved.data['warnings']
    const n = Array.isArray(warnings) ? warnings.length : 0
    log += ` saved ${semver} (${saved.requestId}), ${text.length} bytes, ${n} warning(s)`
    return {
      kept: true,
      says: keptLine(session.title ?? '', semver, n, timesBefore, strings),
      log,
      machineId,
      title: session.title ?? '',
      semver,
      warnings: n,
    }
  }
  return refused(refusedLine('the version could not be written.'), log, session, session.semver)
}
